import { useEffect, useState } from 'react';
import { Globe } from 'lucide-react';

const LOGO = '/images/soursweet-logo.png';
const HERO_IMAGE = '/images/soursweet-kokteyl.png';

export const Experience = () => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('siteLang');
    return saved === 'en' ? 'en' : 'de';
  });

  useEffect(() => {
    document.title = 'SourSweet Experience';
  }, []);

  const toggleLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('siteLang', lang);
  };

  const content = {
    de: {
      title: 'Willkommen bei SourSweet',
      description: 'Folge uns, verbinde dich mit uns und teile deine Erfahrung.',
      buttons: {
        google: 'Google bewerten',
        instagram: 'Instagram',
        twitter: 'Twitter',
        whatsapp: 'WhatsApp',
        website: 'Zur Website'
      }
    },
    en: {
      title: 'Welcome to SourSweet',
      description: 'Follow us, connect with us and share your experience.',
      buttons: {
        google: 'Rate on Google',
        instagram: 'Instagram',
        twitter: 'Twitter',
        whatsapp: 'WhatsApp',
        website: 'Visit Website'
      }
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen relative" data-testid="experience-page">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_IMAGE})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-900/90 via-emerald-900/85 to-emerald-900/95" />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col px-6 pt-0 pb-2">
        {/* Language Switcher - Top Left */}
        <div className="flex justify-start mb-4 pt-3">
          <div className="flex items-center gap-1">
            <button
              onClick={() => toggleLanguage('de')}
              className={`px-3 py-1 text-xs font-bold rounded-full transition-all ${
                language === 'de' 
                  ? 'bg-lime-200 text-emerald-900 shadow-[0_0_20px_rgba(251,146,60,0.6)]' 
                  : 'text-white/70 hover:bg-lime-200 hover:text-emerald-900'
              }`}
            >
              DE
            </button>
            <span className="text-white/40 mx-1">|</span>
            <button
              onClick={() => toggleLanguage('en')}
              className={`px-3 py-1 text-xs font-bold rounded-full transition-all ${
                language === 'en' 
                  ? 'bg-lime-200 text-emerald-900 shadow-[0_0_20px_rgba(251,146,60,0.6)]' 
                  : 'text-white/70 hover:bg-lime-200 hover:text-emerald-900'
              }`}
            >
              EN
            </button>
          </div>
        </div>

        {/* Logo - Centered */}
        <div className="flex justify-center -mt-8 mb-0">
          <img 
            src={LOGO} 
            alt="SourSweet" 
            className="h-52 sm:h-60 md:h-68 w-auto"
            style={{
              filter: 'drop-shadow(0 0 25px rgba(251,146,60,0.8)) drop-shadow(0 0 50px rgba(251,146,60,0.5))'
            }}
          />
        </div>

        {/* Title & Description */}
        <div className="text-center mb-2 -mt-4">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-0">
            {t.title}
          </h1>
          <p className="text-lime-100/80 text-base">
            {t.description}
          </p>
        </div>

        {/* Buttons */}
        <div className="w-full max-w-md mx-auto space-y-4 mt-8">
          {/* Google Button - Standout with warm amber/orange tone */}
          <a
            href="https://g.page/r/CTOPmquS77XaEBM/review"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 w-full bg-amber-100 text-emerald-900 hover:bg-amber-200 hover:scale-[1.02] transition-all duration-300 rounded-2xl px-5 py-3"
            style={{ boxShadow: '0 0 35px rgba(251,146,60,0.7), 0 4px 20px rgba(0,0,0,0.15)' }}
            onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 0 50px rgba(251,146,60,0.9), 0 6px 30px rgba(0,0,0,0.2)'}
            onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 0 35px rgba(251,146,60,0.7), 0 4px 20px rgba(0,0,0,0.15)'}
          >
            <svg width="24" height="24" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span className="text-lg font-bold">{t.buttons.google}</span>
          </a>

          {/* Instagram */}
          <a
            href="https://www.instagram.com/soursweet.de/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 w-full bg-lime-50 text-emerald-900 hover:bg-lime-100 hover:scale-[1.02] transition-all duration-300 rounded-2xl px-5 py-3"
            style={{ boxShadow: '0 0 20px rgba(251,146,60,0.5)' }}
            onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 0 35px rgba(251,146,60,0.8), 0 4px 20px rgba(0,0,0,0.15)'}
            onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 0 20px rgba(251,146,60,0.5)'}
          >
            <svg width="22" height="22" viewBox="0 0 24 24">
              <defs>
                <linearGradient id="ig2" x1="0%" y1="100%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#FFDC80"/><stop offset="25%" stopColor="#F77737"/><stop offset="50%" stopColor="#E1306C"/><stop offset="75%" stopColor="#C13584"/><stop offset="100%" stopColor="#833AB4"/>
                </linearGradient>
              </defs>
              <path fill="url(#ig2)" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
            <span className="text-lg font-bold">{t.buttons.instagram}</span>
          </a>

          {/* Twitter/X */}
          <a
            href="https://x.com/SourSweet_de"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 w-full bg-lime-50 text-emerald-900 hover:bg-lime-100 hover:scale-[1.02] transition-all duration-300 rounded-2xl px-5 py-3"
            style={{ boxShadow: '0 0 20px rgba(251,146,60,0.5)' }}
            onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 0 35px rgba(251,146,60,0.8), 0 4px 20px rgba(0,0,0,0.15)'}
            onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 0 20px rgba(251,146,60,0.5)'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#000000">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            <span className="text-lg font-bold">{t.buttons.twitter}</span>
          </a>

          {/* WhatsApp */}
          <a
            href="https://wa.me/491622755781"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 w-full bg-lime-50 text-emerald-900 hover:bg-lime-100 hover:scale-[1.02] transition-all duration-300 rounded-2xl px-5 py-3"
            style={{ boxShadow: '0 0 20px rgba(251,146,60,0.5)' }}
            onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 0 35px rgba(251,146,60,0.8), 0 4px 20px rgba(0,0,0,0.15)'}
            onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 0 20px rgba(251,146,60,0.5)'}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="#25D366">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            <span className="text-lg font-bold">{t.buttons.whatsapp}</span>
          </a>

          {/* Website */}
          <a
            href="https://soursweet.de"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 w-full bg-lime-50 text-emerald-900 hover:bg-lime-100 hover:scale-[1.02] transition-all duration-300 rounded-2xl px-5 py-3"
            style={{ boxShadow: '0 0 20px rgba(251,146,60,0.5)' }}
            onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 0 35px rgba(251,146,60,0.8), 0 4px 20px rgba(0,0,0,0.15)'}
            onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 0 20px rgba(251,146,60,0.5)'}
          >
            <Globe size={22} className="text-emerald-700" />
            <span className="text-lg font-bold">{t.buttons.website}</span>
          </a>
        </div>

        {/* Contact - Same hover effect as footer email */}
        <div className="text-center mt-6">
          <a 
            href="mailto:info@soursweet.de" 
            className="inline-flex items-center gap-2 text-lime-200 text-base font-semibold hover:bg-lime-200 hover:text-emerald-900 hover:shadow-[0_0_20px_rgba(251,146,60,0.6)] px-4 py-2 rounded-full transition-all duration-300"
          >
            info@soursweet.de
          </a>
        </div>
      </div>
    </div>
  );
};
